import { useEffect, useState, useRef, useMemo } from 'react'
import { Chip, Box, Typography } from '@mui/material'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material'
import {
  Button,
  Col,
  CurrencyInput,
  Grid,
  Input,
  Line,
  Picker,
  Row,
  SelectForm,
  Span,
  AutoCompleteSearchTerm
} from 'components'
import {
  DraggableDialog,
  resetModalState
} from 'components/DraggableDialog/DraggableDialog'
import { useLoading, useModal, useFile } from 'hooks'
import ApplyMasker from 'hooks/useMasker'
import moment from 'moment'
import Draggable from 'react-draggable'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { If } from 'react-if'
import { ConventionServices, IAnexados } from 'services/NGT/Convention'
import { SyndicateServices, IGetFiltroResponse } from 'services/NGT/Syndicate'
import { SPACING, FORMAT } from 'utils'
import * as yup from 'yup'

import { ConsultDocsTable } from '../../../ConsultDocsTable/ConsultDocsTable'
import {
  DialogActionsStyled,
  DialogContentStyled,
  DialogTitleStyled,
  FormDialog
} from './EditConventionModal.style'

interface IEditConventionModal {
  showEditModal: boolean
  toggleModal: () => void
  handleSubmitUpdate: any
  conventionData: any
}

const schema = yup
  .object({
    cdHomologacao: yup.string().required('Informe o Registro de Homologação'),
    dtVigenciaFim: yup
      .string()
      .nullable()
      .required('Informe o fim da vigência'),
    dtVigenciaInicio: yup
      .string()
      .nullable()
      .required('Informe o início da vigência'),
    flBancoHoras: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flHorasExtras: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flTurno: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flValeAlimentacao: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    flValeRefeicao: yup
      .string()
      .min(2, 'Informe Sim ou Não')
      .nullable()
      .required('Informe Sim ou Não'),
    nuAnoMesCompetencia: yup.string().min(4, 'Informe a competência').nullable().required('Informe a competência'),
    txTipo: yup
      .string()
      .min(2, 'Informe o Tipo')
      .nullable()
      .required('Informe o Tipo')
  })
  .required()

const BOOLEAN_FIELDS = [
  'flBancoHoras',
  'flHorasExtras',
  'flTurno',
  'flValeAlimentacao',
  'flValeRefeicao'
]
const PERCENTAGE_FIELDS = [
  'peInsalubridade',
  'pePericulosidade',
  'pePisoSalarial'
]

const EditConventionModal: React.FC<IEditConventionModal> = ({
  showEditModal,
  toggleModal,
  handleSubmitUpdate,
  conventionData
}) => {
  const { setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const { viewFile } = useFile()
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
    register
  } = useForm({
    defaultValues: {
      cdHomologacao: '',
      contratoId: 0,
      dtVigenciaFim: null,
      dtVigenciaInicio: null,
      flBancoHoras: false,
      flHorasExtras: false,
      flTurno: false,
      flValeAlimentacao: false,
      flValeRefeicao: false,
      fornecedorId: 0,
      nuAnoMesCompetencia: '',
      competenciasDisponiveis: [],
      peInsalubridade: 0,
      pePericulosidade: 0,
      pePisoSalarial: 0,
      sindicatos: [{ cnpjSindicato: '', idSindicato: 0, nmSindicato: '' }],
      txObservacaoBancoHoras: '',
      txObservacaoGeral: '',
      txObservacaoHorasExtras: '',
      txObservacaoTurno: '',
      txTipo: '',
      vlReajuste: 0
    },
    resolver: yupResolver(schema)
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sindicatos'
  })
  const watchSindicatos = useWatch({
    control,
    name: 'sindicatos'
  })

  const dtVigenciaInicio = watch('dtVigenciaInicio')
  const watchDtVigenciaFim = watch('dtVigenciaFim')

  const [sindicatos, setSindicatos] = useState<IGetFiltroResponse[]>([])
  const [attachDocsList, setAttachDocsList] = useState<IAnexados[]>([])
  const [isExpired, setIsExpired] = useState<boolean>(conventionData?.flVencido)
  const [competenciasDisponiveis, setCompetenciasDisponiveis] = useState<string[]>([])

  const draggableReference = useRef<Draggable>(null)

  const handleSearchAttachDocs = (idContract: number) => {
    setLoading('loading')
    ConventionServices()
      .getAnexados(idContract)
      .then(({ data }) => {
        setAttachDocsList(
          data.anexos.concat(
            data.anexosPendentes !== null ? data.anexosPendentes : []
          )
        )

        setLoading('idle')
      })
      .catch(() => {
        // setMsgErro('Erro ao carregar lista de arquivos de Acordos e Convenções')
        setLoading('error')
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao carregar lista de arquivos de Acordos e Convenções',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleViewDocument = (externalID: number) => {
    viewFile({
      gedExternalID: externalID
    })
  }

  useEffect(() => {
    if (conventionData) {
      const competencias = [
        conventionData.nuAnoMesCompetencia,
        ...(conventionData.competenciasDisponiveis ?? [])
      ].filter(Boolean)

      setCompetenciasDisponiveis(competencias)
      setValue('nuAnoMesCompetencia', conventionData.nuAnoMesCompetencia)

      Object.keys(conventionData).forEach((fieldName: string) => {
        if (fieldName === 'nuAnoMesCompetencia') return

        const fieldValue = conventionData[fieldName]

        if (BOOLEAN_FIELDS.includes(fieldName)) {
          setValue(fieldName as any, String(fieldValue))
          return
        }

        if (PERCENTAGE_FIELDS.includes(fieldName)) {
          setValue(fieldName as any, String(fieldValue))
          return
        }

        setValue(fieldName as any, fieldValue)
      })
    }


    handleSearchAttachDocs(conventionData?.contratoId)
  }, [conventionData])

  {/*const competenciasFormatadas = Array.from(
  new Set(competenciasDisponiveis)
)
  .filter(Boolean)
  .map((competencia) => FORMAT.competencia(competencia))*/}

  const competenciasFormatadas = useMemo(() => {
    return Array.from(new Set(competenciasDisponiveis))
      .filter(Boolean)
      .map((competencia) => FORMAT.competencia(competencia))
  }, [competenciasDisponiveis])

  const getSindicatos = () => {
    setLoading('loading')

    SyndicateServices()
      .getFiltro({})
      .then((response) => {
        if (response.status === 200) {
          setSindicatos(response.data)
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')

        showModal({
          hideButtonCancel: true,
          message: 'Ocorreu um erro ao obter os sindicatos.',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  interface IHandleSelectSindicado extends IGetFiltroResponse {
    formPosition: number
  }

  const handleSelectSindicato = (props: IHandleSelectSindicado) => {
    if (!props.idSindicato) {
      setValue(`sindicatos.${props.formPosition}.nmSindicato`, '')
      setValue(`sindicatos.${props.formPosition}.cnpjSindicato`, '')
      setValue(`sindicatos.${props.formPosition}.idSindicato`, 0)
      return
    }

    setValue(`sindicatos.${props.formPosition}.nmSindicato`, props.nmSindicato)
    setValue(
      `sindicatos.${props.formPosition}.cnpjSindicato`,
      props.cnpjSindicato
    )
    setValue(`sindicatos.${props.formPosition}.idSindicato`, props.idSindicato)
  }

  useEffect(() => {
    getSindicatos()
  }, [])

  {/*useEffect(() => {
    setIsExpired(moment(watchDtVigenciaFim).toDate() < new Date())
  }, [watchDtVigenciaFim])*/}

  useEffect(() => {
    if (watchDtVigenciaFim) {
      const dataFimVigencia = moment(watchDtVigenciaFim)
      const dataVencimento = dataFimVigencia.clone().add(1, 'month') // Adiciona 1 mês à data de fim da vigência
      const dataAtual = moment()

      setIsExpired(dataAtual.isSameOrAfter(dataVencimento, 'day'))
    }
  }, [watchDtVigenciaFim])

  return (
    <DraggableDialog
      reference={draggableReference}
      showModal={showEditModal}
      onClose={toggleModal}
      width={'xl'}
    >
      <FormDialog
        onSubmit={handleSubmit((data) => {
          if (
            watchSindicatos.filter((sindicato) =>
              Boolean(sindicato.idSindicato)
            ).length === 0
          ) {
            showModal({
              hideButtonCancel: true,
              message: 'Selecione um Sindicato válido',
              txtButtonConfirm: 'OK',
              type: 'error'
            })
            return
          }

          handleSubmitUpdate({
            ...data,
            contratoId: conventionData?.contratoId,
            flVencido: watchDtVigenciaFim < new Date(),
            //flVencido: moment().isSameOrAfter(moment(watchDtVigenciaFim).add(1, 'month'), 'day'),
            fornecedorId: conventionData?.fornecedorId,
            idAcordoConvencao: conventionData?.idAcordoConvencao
          })
          resetModalState(draggableReference)
          toggleModal()
        })}
      >
        <Line />
        <DialogTitleStyled>
          Consultar / Editar Acordo e Convenção
        </DialogTitleStyled>
        <DialogContentStyled>
          <Grid
            gridCols="1fr 1fr 4fr 1.5fr"
            gridGap={`0 ${SPACING.spacing15}`}
            margin={`${SPACING.spacing15} 0 ${SPACING.spacing20} 0`}
          >
            <Input
              label="Contrato"
              placeholder="Contrato"
              value={conventionData?.cdContrato}
              disabled
            />
            <Input
              label="CPF/CNPJ/Cód.Fornecedor"
              placeholder="Nº"
              value={conventionData?.cnpjFornecedor}
              mask={
                conventionData?.cnpjFornecedor && conventionData?.cnpjFornecedor.length > 9
                  ? ['###.###.###-##', '##.###.###/####-##']
                  : ['#########']

              }
              required
              disabled
            />
            <Input
              formValue="nmFornecedor"
              label="Fornecedor"
              placeholder="Nome do Fornecedor"
              value={conventionData?.nmFornecedor}
              required
              disabled
            />
          </Grid>

          <Grid
            gridCols="1fr 1fr 4fr 1.5fr"
            gridGap={`0 ${SPACING.spacing15}`}
            margin={`0 0 ${SPACING.spacing30} 0`}
          >
            <SelectForm
              control={control}
              errors={errors}
              formValue="txTipo"
              defaultValue={conventionData?.txTipo}
              label="Tipo"
              listOptions={[
                { label: 'Acordo', value: 'Acordo' },
                { label: 'Convenção', value: 'Convencao' }
              ]}
              placeholder="Tipo"
              required
            />
            <Input
              marginTop="3px"
              control={control}
              errors={errors}
              defaultValue={conventionData?.cdHomologacao}
              label="Registro de Homologação"
              placeholder="Nº do Registro"
              formValue="cdHomologacao"
              required
            />
            {/*<SelectForm
              marginTop="0.1rem"
              control={control}
              errors={errors}
              formValue="nuAnoMesCompetencia"
              label="Competência"
              defaultValue={conventionData?.nuAnoMesCompetencia}
              listOptions={competenciasDisponiveis?.map((competencia) => (
                { label: FORMAT.competencia(competencia), value: competencia }
              ))}
              labelMenu="label"
              valueMenu="value"
              placeholder="Competência"
              required
            />*/}
            <Box>
              <Typography
                variant="caption"
                sx={{ display: 'block', marginBottom: '4px' }}
              >
                Competências
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  padding: '8px 12px',
                  border: '1px solid #c4c4c4',
                  borderRadius: '4px',
                  minHeight: '40px',
                  backgroundColor: '#fafafa',
                  cursor: 'not-allowed',
                  opacity: 0.9
                }}
              >
                {competenciasFormatadas.map((competencia) => (
                  <Chip
                    key={competencia}
                    label={competencia}
                    size="small"
                  />
                ))}
              </Box>
            </Box>

            <input
              type="hidden"
              {...register('nuAnoMesCompetencia')}
            />
          </Grid>
          <>
            {fields.map((field, index) => (
              <Grid
                key={field.id}
                gridCols="3fr 4fr 0.2fr 0.2fr 1.9fr"
                gridGap={`0 ${SPACING.spacing15}`}
                margin={`0 0 ${SPACING.spacing30} 0`}
              >
                <Col>
                  <AutoCompleteSearchTerm
                    control={control}
                    errors={errors}
                    onSelect={(selectedValue: IGetFiltroResponse) =>
                      handleSelectSindicato({
                        ...selectedValue,
                        formPosition: index
                      })
                    }
                    formValue={`sindicatos.${index}.cnpjSindicato`}
                    options={
                      sindicatos.map((sindicato) => ({
                        ...sindicato,
                        cdCnpj: ApplyMasker(sindicato.cdCnpj, [
                          '##.###.###/####-##'
                        ]),
                        cnpjSindicato: sindicato.cdCnpj
                      })) || []
                    }
                    optionLabel="cdCnpj"
                    optionValue="cnpjSindicato"
                    label="CNPJ Sindicato"
                  />
                </Col>

                <Col>
                  <AutoCompleteSearchTerm
                    control={control}
                    errors={errors}
                    onSelect={(selectedValue: IGetFiltroResponse) =>
                      handleSelectSindicato({
                        ...selectedValue,
                        formPosition: index
                      })
                    }
                    formValue={`sindicatos.${index}.nmSindicato`}
                    options={
                      sindicatos.map((sindicato) => ({
                        ...sindicato,
                        cdCnpj: ApplyMasker(sindicato.cdCnpj, [
                          '##.###.###/####-##'
                        ]),
                        cnpjSindicato: sindicato.cdCnpj
                      })) || []
                    }
                    optionLabel="nmSindicato"
                    optionValue="nmSindicato"
                    label="Sindicato / Confederação"
                  />
                </Col>
                <input
                  style={{ display: 'none' }}
                  {...register(`sindicatos.${index}.idSindicato`)}
                />

                <Button
                  text="+"
                  onClick={() =>
                    append({
                      cnpjSindicato: '',
                      idSindicato: 0,
                      nmSindicato: ''
                    })
                  }
                />
                <If condition={index > 0}>
                  <Button text="-" onClick={() => remove(index)} />
                </If>
              </Grid>
            ))}
          </>
          <Grid
            gridCols="1fr 1fr 1fr 1fr 1fr 1fr 1.5fr"
            gridGap={`0 ${SPACING.spacing15}`}
            margin={`0 0 ${SPACING.spacing30} 0`}
          >
            <CurrencyInput
              control={control}
              errors={errors}
              style="percent"
              label="Periculosidade %"
              placeholder="%"
              maxLength={5}
              formValue="pePericulosidade"
              required
            />
            <CurrencyInput
              control={control}
              errors={errors}
              style="percent"
              label="Insalubridade %"
              placeholder="%"
              maxLength={5}
              formValue="peInsalubridade"
              required
            />
            <CurrencyInput
              control={control}
              errors={errors}
              label="Piso Salarial"
              placeholder="%"
              formValue="pePisoSalarial"
              required
            />
            <CurrencyInput
              control={control}
              errors={errors}
              style="percent"
              label="Reajuste %"
              placeholder="%"
              defaultValue={conventionData?.vlReajuste}
              maxLength={5}
              formValue="vlReajuste"
              required
            />
            <SelectForm
              control={control}
              errors={errors}
              formValue="flValeAlimentacao"
              label="Vale Alimentação"
              listOptions={[
                { label: 'Sim', value: 'true' },
                { label: 'Não', value: 'false' }
              ]}
              labelMenu="label"
              valueMenu="value"
              placeholder="Vale Alimentação"
              required
            />
            <SelectForm
              control={control}
              errors={errors}
              formValue="flValeRefeicao"
              label="Vale Refeição"
              listOptions={[
                { label: 'Sim', value: 'true' },
                { label: 'Não', value: 'false' }
              ]}
              labelMenu="label"
              valueMenu="value"
              placeholder="Vale Refeição"
              required
            />
          </Grid>
          <Grid
            gridCols="1fr 2fr 1fr 2fr 1.5fr"
            gridGap={`0 ${SPACING.spacing15}`}
            margin={`0 0 ${SPACING.spacing30} 0`}
          >
            <SelectForm
              control={control}
              errors={errors}
              formValue="flHorasExtras"
              label="Horas Extras"
              listOptions={[
                { label: 'Sim', value: 'true' },
                { label: 'Não', value: 'false' }
              ]}
              labelMenu="label"
              valueMenu="value"
              placeholder="Horas Extras"
              required
            />
            <Input
              control={control}
              label="Obs. Horas Extras"
              placeholder="Observação"
              defaultValue={conventionData?.txObservacaoHorasExtras}
              formValue="txObservacaoHorasExtras"
            />
            <SelectForm
              control={control}
              errors={errors}
              formValue="flBancoHoras"
              label="Banco de Horas"
              listOptions={[
                { label: 'Sim', value: 'true' },
                { label: 'Não', value: 'false' }
              ]}
              labelMenu="label"
              valueMenu="value"
              placeholder="Banco de Horas"
              required
            />
            <Input
              control={control}
              label="Obs. Banco de Horas"
              placeholder="Observação"
              defaultValue={conventionData?.txObservacaoBancoHoras}
              formValue="txObservacaoBancoHoras"
            />
          </Grid>
          <Grid
            gridCols="1fr 2fr 3fr 1.5fr"
            gridGap={`0 ${SPACING.spacing15}`}
            margin={`0 0 ${SPACING.spacing5} 0`}
          >
            <SelectForm
              control={control}
              errors={errors}
              formValue="flTurno"
              label="Turno"
              listOptions={[
                { label: 'Sim', value: 'true' },
                { label: 'Não', value: 'false' }
              ]}
              labelMenu="label"
              valueMenu="value"
              placeholder="Turno"
              required
            />
            <Input
              control={control}
              label="Obs. Turno"
              placeholder="Observação"
              defaultValue={conventionData?.txObservacaoTurno}
              formValue="txObservacaoTurno"
            />
            <Input
              control={control}
              label="Obs. Gerais"
              placeholder="Observação"
              defaultValue={conventionData?.txObservacaoGeral}
              formValue="txObservacaoGeral"
            />
          </Grid>
          <Grid
            gridCols="1fr 1fr 1fr 4.5fr"
            gridGap={`0 ${SPACING.spacing15}`}
            margin={`${SPACING.spacing30} 0 ${SPACING.spacing5} 0`}
          >
            <Picker
              defaultValue={conventionData?.dtVigenciaInicio}
              label="Início Vigência"
              formValue="dtVigenciaInicio"
              maxDate={moment(watchDtVigenciaFim)}
              control={control}
              errors={errors}
            />
            <Picker
              defaultValue={conventionData?.dtVigenciaFim}
              label="Fim Vigência"
              formValue="dtVigenciaFim"
              minDate={moment(dtVigenciaInicio)}
              control={control}
              errors={errors}
            />
            <Col marginLeft={SPACING.spacing15}>
              <Span text="Vencido" />
              {isExpired ? (
                <Row marginTop={SPACING.spacing10}>
                  <CheckBoxIcon color="primary" />
                  <Span
                    marginTop={SPACING.spacing5}
                    marginLeft={SPACING.spacing5}
                    marginRight={SPACING.spacing5}
                    text="Sim"
                  />
                  <CheckBoxOutlineBlankIcon color={'disabled'} />
                  <Span
                    marginTop={SPACING.spacing5}
                    marginLeft={SPACING.spacing5}
                    marginRight={SPACING.spacing5}
                    text="Não"
                  />
                </Row>
              ) : (
                <Row marginTop={SPACING.spacing10}>
                  <CheckBoxOutlineBlankIcon color={'disabled'} />
                  <Span
                    marginTop={SPACING.spacing5}
                    marginLeft={SPACING.spacing5}
                    marginRight={SPACING.spacing5}
                    text="Sim"
                  />
                  <CheckBoxIcon color="primary" />
                  <Span
                    marginTop={SPACING.spacing5}
                    marginLeft={SPACING.spacing5}
                    marginRight={SPACING.spacing5}
                    text="Não"
                  />
                </Row>
              )}
            </Col>
          </Grid>
        </DialogContentStyled>
        <ConsultDocsTable
          attachDocsList={attachDocsList}
          handleViewDocument={handleViewDocument}
        />
        <DialogActionsStyled>
          <Button
            text="Voltar"
            variantButton="outlined"
            onClick={() => {
              toggleModal()
              resetModalState(draggableReference)
            }}
            marginBottom={SPACING.spacing15}
          />
          <Row justifyContent="flex-end">
            <Button
              text="SALVAR"
              type="submit"
              marginLeft={SPACING.spacing20}
              marginRight={SPACING.spacing20}
              marginBottom={SPACING.spacing15}
            />
          </Row>
        </DialogActionsStyled>
      </FormDialog>
    </DraggableDialog>
  )
}

export default EditConventionModal
